<template>
    <div class="goog-map">
        <GmapMap v-if="getGmapAPIKey"
            :options=" {
                mapTypeControlOptions: { mapTypeIds: [] },
				disableDefaultUI: true,
            }"
            :center="getCenter()"
            ref="baseMap" 
            :zoom="zoom"
            :style="`width: ${width}; height: ${height};`">
			<div v-for="resturantLocation in locations" :key="resturantLocation.id">
				<google-marker-info-window
					v-if="!directions && googleLoaded"
					:location="resturantLocation"
					:icon="icons.destination"
					:pixelOffset="getPixelOffset()"
					:focusedLocationBase="getFocusedLocationBase()"
					@click="handleMarkerInfoClick(resturantLocation)"/>
			</div>
			<google-marker-info-window
					v-if="!directions && googleLoaded && storeLocation"
					:location="storeLocation"
					:icon="getMarkerIcon()"
					:pixelOffset="getPixelOffset()"
					:focusedLocationBase="getFocusedLocationBase()"
					@click.native="emit('click')"/>
        </GmapMap>
    </div>
</template>

<script>
import EventBus from '@EventBus';
import * as storeMarker from "@/assets/icons/maps/store-marker.png";
import * as homeMarker from "@/assets/icons/maps/home-marker.png";
import { loaded } from 'vue2-google-maps';
import mapsMixin from '@/components/mixins/mapsMixin';
import locationsMixin from '@Mixin/locationsMixin';
import GoogleMarkerInfoWindow from "@/components/store-locator/GoogleMarkerInfoWindow";
import { lightenDarkenHex } from '@/utils/helper.functions';
import { isEmpty } from 'lodash';

const markerIcons = {
	home: homeMarker,
	destination: storeMarker,
}

export default {
	mixins: [
		mapsMixin,
		locationsMixin,
	],
	props: {
		storeLocation: {
			type: Object,
		},
		locations: {
			type: Array,
			default: () => {
				return [];
			}
		},
        width: {
            type: String,
            default: "100%"
        },
        height: {
            type: String,
            default: "200px"
        },
        zoom: {
            type: Number,
            default: 10
        },
        directions: {
            type: Object,
            default: null
		},
		focusedCenter: {
			type: Object,
			default: null,
		},
		icons: {
			type: Object,
			default: () => {
				return markerIcons;
			}
		}
    },
    components: {
        "google-marker-info-window": GoogleMarkerInfoWindow
    },
    data() {
        return {
            center: null,
            directionRenderer: null,
            directionsService: null,
            geocoder: null,
            map: null,
            markers: [],
            locationRadii: [],
            routeCreated: false,
        }
    },
    methods: {
        createRoute() {
            if(this.directions.origin && this.directions.destination) {
                this.$refs.baseMap.$mapPromise
                    .then((map) => {
                        this.map = map;
                        if (!this.directionsService && !this.directionsRenderer) {
                            this.directionsService = new google.maps.DirectionsService();
                            this.directionRenderer = new google.maps.DirectionsRenderer();
                            this.directionRenderer.setMap(map);
                            // remove default markers
                            this.directionRenderer.setOptions( { suppressMarkers: true } );
                        }

                        this.generateLocationRadii();
                        const origin = new google.maps.LatLng(parseFloat(this.directions.origin.latitude), parseFloat(this.directions.origin.longitude));
                        let destination;
                        if (!this.directions.destination.latitude || !this.directions.destination.longitude) {
                            if (!this.geocoder) {
                                this.createGeocoder();
                            }

                            this.geocoder.geocode({'address': this.directions.destination.formattedAddress}, (results, status) => {
                                if(status === 'OK') {
                                    this.directions.destination.latitude = results[0].geometry.location.lat();
                                    this.directions.destination.longitude = results[0].geometry.location.lng();
                                    destination = new google.maps.LatLng(parseFloat(this.directions.destination.latitude), parseFloat(this.directions.destination.longitude));
                                    this.routeRender(origin, destination);

                                    this.fitBounds(origin, destination)
                                } else {
                                    console.log(status);
                                }
                            })
                        } else {
                            destination = new google.maps.LatLng(parseFloat(this.directions.destination.latitude), parseFloat(this.directions.destination.longitude));
                            this.routeRender(origin, destination);
                            this.fitBounds(origin, destination)
                        }

                    })
                    .catch((err) => {
                        console.log(err);
                    });
            }
        },
        routeRender(origin, destination) {
            this.clearMarkers();
            // render customer markers
            this.renderMarker(origin, storeMarker, "")
            this.renderMarker(destination, homeMarker, "")

            this.directionsService.route({
                origin: origin,
                destination: destination,
                travelMode: 'DRIVING'
            }, (result, status) => {
                if (status === "OK") {
                    this.directionRenderer.setDirections(result);
                } else {
                    console.log(result);
                }
            });
        },
        renderMarker(position, icon, title) {
            let marker = new google.maps.Marker({
                position: position,
                map: this.map,
                icon: icon
            });
            marker.setDraggable = false;
            this.markers.push(marker);
        },
        clearMarkers() {
            for (let i = 0; i < this.markers.length; i++) {
                this.markers[i].setMap(null);
            }
            this.markers = [];
        },
        generateLocationRadii() {
            if (this.map && this.directions.origin.delivery.hasDeliveryRegionsSet) {
                let radiusHexColor = '#9fd7fb';
                for (let i = 0; i < this.directions.origin.delivery.deliveryRegions.length; i++) {
                    // change radius color
                    radiusHexColor = (i !== 0) ? lightenDarkenHex(radiusHexColor, 0.3) : radiusHexColor;

                    this.locationRadii.push(
                        new google.maps.Polygon({
                            paths: this.directions.origin.delivery.deliveryRegions[i].processedPoints,
                            strokeColor: radiusHexColor,
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: radiusHexColor,
                            fillOpacity: 0.35
                        })
                    )
                    this.locationRadii[i].setMap(this.map)
                }
            }
        },
        clearRadii() {
             for (let i = 0; i < this.locationRadii.length; i++) {
                this.locationRadii[i].setMap(null);
            }
            this.locationRadii = [];
        },
        createGeocoder() {
            this.geocoder = new google.maps.Geocoder();
        },
        fitBounds(origin, destination) {
           let bounds = new google.maps.LatLngBounds(destination, origin)
           this.map.fitBounds(bounds);
		},
		getCenter() {
			if (this.focusedCenter) {
				return {
					lat: this.focusedCenter.latitude,
					lng: this.focusedCenter.longitude,
				};
			}
			const location = this.location || this.locations[0];
			if (!location) {
				//use franchisor location?
				return {
					lat: 0,
					lng: 0,
				};
			}
			return {
				lat: parseFloat(location.latitude),
				lng: parseFloat(location.longitude),
			}
		},
		getMarkerIcon() {
			const markLocation = this.location;
			const icons = this.icons;
			if (!markLocation) {
				return icons.destination;
			}
			if (!isEmpty(this.locations)) {
				return icons.home;
			}
			const directions = this.directions;
			if (markLocation.__mc === 'LocationModel' && !directions) {
				return icons.destination;
			}
			return icons.home;
		},
		getPixelOffset() {
			const location = this.location;
			if (!location) {
				return null;
			}
			if (location.__mc === 'LocationModel') {
				return {
					width: 0,
					height: -70,
				};
			}
			return null;
		},
		getFocusedLocationBase() {
			const location = this.location;
			if (!location) {
				return '';
			}
			return location.base || location.id;
		},
		handleMarkerInfoClick(location) {
			EventBus.$emit('map-marker-clicked', location);
		}
    },
    mounted() {
        if (this.googleLoaded && this.appLoaded) {
            if (this.directions && this.directions.origin && this.directions.destination) {
                this.createRoute();
            }
        }
    },
    watch: {
        // triggers while user is switching addresses or servicetype
        directions(newValue, oldValue) {
			const hasNewDestination = typeof newValue.destination === 'object';
			const hasOldDestination = typeof oldValue.destination === 'object';
			const hasDestinations = hasNewDestination && hasOldDestination;
			if (hasDestinations && newValue.destination.base === oldValue.destination.base) {
				// same destination don't update
				return;
			}
            if ( this.googleLoaded && this.appLoaded && (newValue && newValue.origin !== null && newValue.destination !== null)) {
                this.createRoute();
            } else {
                if (this.directionRenderer) {
                    this.directionRenderer.set('directions', null);
                    this.clearMarkers();
                    this.clearRadii();
                }
            }
        },
        googleLoaded (newVal, oldVal) {
            if (newVal) {
                if (this.appLoaded && (this.directions && this.directions.origin !== null && this.directions.destination !== null)) {
                    this.createRoute();
                }
            }
        }
    },
}
</script>

<style lang="scss">
	@import 'globalStyles';
    .goog-map {
        padding-bottom: 1em;
	}
	@include mobile {
		.goog-map {
			padding-bottom: 0;
		}
	}
</style>



// WEBPACK FOOTER //
// src/components/common/BaseMap.vue